import { useCallback } from 'react';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import {
  DocumentRenewalReminderTemplateUpdateInputObject,
  DocumentationRenewalRepetitionEnum,
  useEdmDocumentRenewalReminderTemplateUpdateMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { Values } from '@lib/interfaces/form';

function toAttributes(
  values: Values,
): DocumentRenewalReminderTemplateUpdateInputObject {
  const enabled = !!_get(values, DocumentFormFieldNames.RenewalReminderEnabled);
  if (!enabled) {
    return {
      renewalRepetition: null,
      renewalRepetitionCustomDays: null,
      renewalEarlyReminderDays: null,
      renewalDeadlineMd: null,
    };
  }
  const repetition = _get(values, [
    DocumentFormFieldNames.RenewalReminderRepetition,
    'value',
  ]) as DocumentationRenewalRepetitionEnum | undefined;
  const customDays = _get(
    values,
    DocumentFormFieldNames.RenewalReminderCustomDays,
  );
  const earlyAmount = _get(
    values,
    DocumentFormFieldNames.RenewalReminderEarlyAmount,
  );
  const deadlineMd = _get(
    values,
    DocumentFormFieldNames.RenewalReminderDeadlineDate,
  ) as string | undefined;
  return {
    renewalRepetition: repetition ?? null,
    renewalRepetitionCustomDays:
      repetition === DocumentationRenewalRepetitionEnum.Custom && customDays
        ? Number(customDays)
        : null,
    renewalEarlyReminderDays: earlyAmount ? Number(earlyAmount) : null,
    renewalDeadlineMd: deadlineMd || null,
  };
}

export default function useReminderActions() {
  const { t } = useTranslation();
  const [updateMutation, updateResult] =
    useEdmDocumentRenewalReminderTemplateUpdateMutation();

  const onSubmitForm = useCallback(
    (values: Values) => {
      const attributes = toAttributes(values);
      updateMutation({
        variables: { attributes },
        onCompleted: () => {
          toast({ content: t('reminder-saved-toast') });
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    },
    [t, updateMutation],
  );

  return {
    loading: updateResult.loading,
    onSubmitForm,
  };
}
